<template>
  <div class="cf-audio" :class="{ 'cf-audio--compact': compact }">
    <audio
      ref="audio"
      :src="src"
      preload="none"
      @loadedmetadata="onMeta"
      @timeupdate="onTime"
      @ended="onEnded"
      @play="playing = true"
      @pause="playing = false"
      @error="onError"
    ></audio>

    <!-- play / pause -->
    <button
      type="button"
      class="cf-audio__btn"
      :disabled="errored"
      @click="toggle"
    >
      <Icon :icon="errored ? 'mdi:alert-circle-outline' : (playing ? 'mdi:pause' : 'mdi:play')" />
    </button>

    <!-- progress + time -->
    <div class="cf-audio__mid">
      <input
        type="range"
        class="cf-audio__range"
        min="0"
        :max="seekMax"
        step="0.1"
        :value="currentTime"
        :disabled="errored"
        :style="rangeStyle"
        @input="seek($event.target.value)"
      />
      <div class="cf-audio__time">
        <span>{{ fmt(currentTime) }}</span>
        <span>{{ errored ? '--:--' : fmt(displayDuration) }}</span>
      </div>
    </div>

    <!-- playback speed -->
    <button
      type="button"
      class="cf-audio__speed"
      :disabled="errored"
      @click="cycleSpeed"
    >
      {{ speed }}x
    </button>
  </div>
</template>

<script>
export default {
  name: "AudioPlayer",
  props: {
    src: { type: String, required: true },
    // Backend-provided duration (seconds) — shown until the audio's own
    // metadata loads (we keep preload="none" to avoid fetching every clip).
    initialDuration: { type: [Number, String], default: 0 },
    // Denser layout for tight spots (e.g. an orders-list table cell).
    compact: { type: Boolean, default: false },
  },
  data() {
    return {
      playing: false,
      errored: false,
      currentTime: 0,
      duration: 0,
      speed: 1,
      speeds: [1, 1.5, 2],
    };
  },
  computed: {
    displayDuration() {
      if (this.duration && isFinite(this.duration)) return this.duration;
      return Number(this.initialDuration) || 0;
    },
    seekMax() {
      return this.displayDuration || 0;
    },
    rangeStyle() {
      const d = this.displayDuration;
      const pct = d ? Math.min(100, (this.currentTime / d) * 100) : 0;
      return {
        background: `linear-gradient(to right, #5743BE 0%, #5743BE ${pct}%, #e2e8f0 ${pct}%, #e2e8f0 100%)`,
      };
    },
  },
  methods: {
    toggle() {
      const a = this.$refs.audio;
      if (!a || this.errored) return;
      if (a.paused) {
        this.pauseOthers();
        a.playbackRate = this.speed;
        const p = a.play();
        if (p && p.catch) p.catch(() => { this.errored = true; });
      } else {
        a.pause();
      }
    },
    // Only one recording plays at a time across the whole page.
    pauseOthers() {
      const me = this.$refs.audio;
      document.querySelectorAll("audio").forEach((el) => {
        if (el !== me) el.pause();
      });
    },
    seek(v) {
      const a = this.$refs.audio;
      const t = Number(v) || 0;
      if (a && isFinite(a.duration)) a.currentTime = t;
      this.currentTime = t;
    },
    onMeta() {
      const a = this.$refs.audio;
      if (a && isFinite(a.duration)) this.duration = a.duration;
    },
    onTime() {
      const a = this.$refs.audio;
      if (a) this.currentTime = a.currentTime || 0;
    },
    onEnded() {
      this.playing = false;
      this.currentTime = 0;
    },
    onError() {
      this.errored = true;
      this.playing = false;
    },
    cycleSpeed() {
      const i = this.speeds.indexOf(this.speed);
      this.speed = this.speeds[(i + 1) % this.speeds.length];
      if (this.$refs.audio) this.$refs.audio.playbackRate = this.speed;
    },
    fmt(s) {
      s = Math.floor(Number(s) || 0);
      const m = Math.floor(s / 60);
      const r = s % 60;
      return `${m}:${String(r).padStart(2, "0")}`;
    },
  },
};
</script>

<style scoped>
.cf-audio {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border-radius: 12px;
  background: #f1f5f9;
  box-sizing: border-box;
}
.cf-audio__btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  color: #fff;
  background: #5743be;
  font-size: 18px;
  transition: transform 0.1s ease, background 0.15s ease;
}
.cf-audio__btn:hover:not(:disabled) { background: #4736a3; }
.cf-audio__btn:active:not(:disabled) { transform: scale(0.92); }
.cf-audio__btn:disabled { background: #cbd5e1; cursor: not-allowed; }

.cf-audio__mid {
  flex: 1 1 auto;
  min-width: 0; /* lets the range shrink instead of overflowing */
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.cf-audio__range {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 5px;
  border-radius: 9999px;
  outline: none;
  cursor: pointer;
  background: #e2e8f0;
}
.cf-audio__range:disabled { cursor: not-allowed; opacity: 0.6; }
.cf-audio__range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 13px;
  height: 13px;
  border-radius: 9999px;
  background: #5743be;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  cursor: pointer;
}
.cf-audio__range::-moz-range-thumb {
  width: 13px;
  height: 13px;
  border-radius: 9999px;
  background: #5743be;
  border: 2px solid #fff;
  cursor: pointer;
}
.cf-audio__time {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  line-height: 1;
  color: #64748b;
  font-variant-numeric: tabular-nums;
}
.cf-audio__speed {
  flex: 0 0 auto;
  min-width: 38px;
  padding: 4px 6px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-variant-numeric: tabular-nums;
}
.cf-audio__speed:hover:not(:disabled) { background: #f8fafc; border-color: #94a3b8; }
.cf-audio__speed:disabled { opacity: 0.5; cursor: not-allowed; }

/* Compact variant — dense list cells (orders table). */
.cf-audio--compact {
  gap: 7px;
  padding: 5px 7px;
  border-radius: 9px;
}
.cf-audio--compact .cf-audio__btn {
  width: 26px;
  height: 26px;
  font-size: 15px;
}
.cf-audio--compact .cf-audio__range {
  height: 4px;
}
.cf-audio--compact .cf-audio__range::-webkit-slider-thumb {
  width: 10px;
  height: 10px;
}
.cf-audio--compact .cf-audio__range::-moz-range-thumb {
  width: 10px;
  height: 10px;
}
.cf-audio--compact .cf-audio__time {
  font-size: 10px;
}
.cf-audio--compact .cf-audio__speed {
  min-width: 30px;
  padding: 2px 4px;
  font-size: 11px;
}

/* very narrow screens — keep it usable */
@media (max-width: 360px) {
  .cf-audio { gap: 7px; padding: 7px 8px; }
  .cf-audio__btn { width: 30px; height: 30px; font-size: 16px; }
  .cf-audio__speed { min-width: 34px; padding: 4px; }
}
</style>
